'use client'

import React from 'react'
import { Inter, Karla } from 'next/font/google'
import { ColorModeScript } from '@chakra-ui/react'
import theme from './theme'
import Button from '@/components/livecontact/Button'
import EmptyState from '@/components/livecontact/EmptyState'
import LcIcon from '@/components/livecontact/icons'
import './globals.css'

const inter = Inter({
  weight: ['500', '600', '700'],
  subsets: ['latin'],
  display: 'block',
  variable: '--font-inter',
})

const karla = Karla({
  weight: ['400', '700'],
  subsets: ['latin'],
  display: 'block',
  variable: '--font-karla',
})

type Props = {
  error: Error & { digest?: string }
  reset: () => void
}

const DESCRIPTION =
  'Something went wrong on our end. Please try again later or contact support if the issue persists.'

/**
 * Root error boundary. It replaces the root layout, so it re-declares the document shell
 * (fonts, globals, colour-mode script) and renders the system's centred error state.
 */
const GlobalError = ({ error, reset }: Props) => {
  const { name, message, stack, digest, ...rest } = error
  const isProd = process.env.NEXT_PUBLIC_APP_ENV === 'production'

  return (
    <html lang="en" suppressHydrationWarning>
      <head>
        <title>500 Internal Server Error | LiveContact</title>
        <meta name="description" content={DESCRIPTION} />
        <meta property="og:title" content="500 Internal Server Error | LiveContact" />
        <meta property="og:description" content={DESCRIPTION} />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="/icon.jpg" />
        <link rel="icon" href="/icon.jpg" type="image/jpeg" />
      </head>
      <body
        suppressHydrationWarning
        className={`${karla.variable} ${inter.variable} lc-root bg-canvas font-lc text-[1rem] leading-6 -tracking-[.01em] text-ink antialiased`}
      >
        <ColorModeScript initialColorMode={theme.config.initialColorMode} />
        <main className="flex min-h-screen flex-col items-center justify-center gap-8 px-6 py-16">
          <EmptyState
            icon={<LcIcon name="alert" size={40} />}
            title="Something went wrong"
            description="An unexpected error interrupted this page. Trying again usually fixes it."
            action={
              <div className="flex items-center gap-2">
                <Button size="lg" onClick={reset}>
                  Try again
                </Button>
                <Button size="lg" variant="secondary" asChild>
                  <a href="/">Go to home</a>
                </Button>
              </div>
            }
          />

          {!isProd && (
            <section className="w-full max-w-[640px] rounded-[16px] bg-surface p-5 shadow-input animate-lc-fade-up">
              <p className="text-[14px] font-semibold leading-[1.2] tracking-[-0.2px] text-ink">
                Error details
              </p>
              <dl className="mt-3 grid grid-cols-[auto_1fr] gap-x-4 gap-y-1.5 text-[12px] leading-[1.45] tracking-[-0.2px]">
                <dt className="font-medium text-ink-3">Name</dt>
                <dd className="break-words text-ink">{name}</dd>
                <dt className="font-medium text-ink-3">Message</dt>
                <dd className="break-words text-ink">{message}</dd>
                {digest && (
                  <>
                    <dt className="font-medium text-ink-3">Code</dt>
                    <dd className="font-mono text-ink">{digest}</dd>
                  </>
                )}
              </dl>
              {stack && (
                <pre className="mt-3 max-h-[240px] overflow-auto whitespace-pre-wrap break-words rounded-[12px] bg-canvas p-3 font-mono text-[11px] leading-[1.5] text-ink-2 dark:bg-surface-2">
                  {stack}
                </pre>
              )}
              {Object.keys(rest).length > 0 && (
                <details className="mt-3 text-[12px] text-ink-3">
                  <summary className="cursor-pointer font-medium outline-none focus-visible:ring-2 focus-visible:ring-brand/40">
                    Additional info
                  </summary>
                  <pre className="mt-2 whitespace-pre-wrap break-words rounded-[12px] bg-canvas p-3 font-mono text-[11px] leading-[1.5] text-ink-2 dark:bg-surface-2">
                    {JSON.stringify(rest, null, 2)}
                  </pre>
                </details>
              )}
            </section>
          )}
        </main>
      </body>
    </html>
  )
}

export default GlobalError
